<template>
  <div v-if="isOpen" class="camera-capture">
    <video
      ref="videoEl"
      class="camera-capture__video"
      autoplay
      playsinline
      muted
    ></video>

    <!-- Thin bar, houses the close button. Top in portrait, left in landscape. -->
    <div class="camera-capture__bar camera-capture__bar--secondary">
      <button
        type="button"
        class="camera-capture__close"
        aria-label="Fermer la caméra"
        @click="cancel"
      >
        <ion-icon :icon="closeIcon" />
      </button>
    </div>

    <!-- Thick bar: file picker, shutter (main action), and a spacer to
    keep the shutter visually centered. Bottom in portrait, right in
    landscape — mirrors the native camera app's layout. -->
    <div class="camera-capture__bar camera-capture__bar--primary">
      <button
        type="button"
        class="camera-capture__file"
        aria-label="Choisir un fichier"
        @click="pickFile"
      >
        <ion-icon :icon="imageIcon" />
      </button>

      <button
        type="button"
        class="camera-capture__shutter"
        aria-label="Prendre la photo"
        :disabled="!streamReady"
        @click="capture"
      />

      <div class="camera-capture__spacer" aria-hidden="true"></div>
    </div>

    <input
      ref="fileInputEl"
      type="file"
      accept="image/*"
      class="camera-capture__file-input"
      @change="onFileSelected"
    />
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import { IonIcon } from '@ionic/vue';
import { close as closeIcon, imageOutline as imageIcon } from 'ionicons/icons';
import { useCameraCapture } from '@/app/composables/useCameraCapture';
import { resizeImage } from '@/app/utils/image';

const { isOpen, resolveCameraCapture } = useCameraCapture();
const videoEl = ref<HTMLVideoElement>();
const fileInputEl = ref<HTMLInputElement>();
const streamReady = ref(false);
let stream: MediaStream | undefined;

// enumerateDevices() lists device kinds without requiring permission first,
// so this lets us skip straight to the file picker on hardware with no
// camera at all (e.g. a fixed desktop PC) instead of calling getUserMedia
// and leaving the user stuck on a dead video screen with a permanently
// disabled shutter.
const hasCamera = async (): Promise<boolean> => {
  if (!navigator.mediaDevices?.enumerateDevices) {
    return true;
  }

  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.some((device) => device.kind === 'videoinput');
  } catch {
    return true;
  }
};

const startStream = async () => {
  streamReady.value = false;

  if (!(await hasCamera())) {
    await nextTick();
    pickFile();
    return;
  }

  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment' },
      audio: false,
    });
  } catch {
    // Camera present but unreachable (permission denied/blocked, already in
    // use by another app...) — same dead-end as no camera at all, so fall
    // back to the file picker instead of just closing the screen.
    await nextTick();
    pickFile();
    return;
  }

  await nextTick();
  if (videoEl.value) {
    videoEl.value.srcObject = stream;
    streamReady.value = true;
  }
};

const stopStream = () => {
  stream?.getTracks().forEach((track) => track.stop());
  stream = undefined;
  streamReady.value = false;
};

watch(isOpen, (open) => {
  if (open) {
    startStream();
  } else {
    stopStream();
  }
});

const capture = async () => {
  if (!videoEl.value) {
    return;
  }

  const canvas = document.createElement('canvas');
  canvas.width = videoEl.value.videoWidth;
  canvas.height = videoEl.value.videoHeight;
  canvas.getContext('2d')?.drawImage(videoEl.value, 0, 0);

  const dataUrl = canvas.toDataURL('image/jpeg', 0.9);

  try {
    // A draft holds 12+ mandatory photos plus any number of damage/info
    // shots, all kept as base64 in local storage until final send (see
    // piniaCapacitorPersist.ts) — 1920px/q0.8 was still exceeding storage
    // quota on some devices even after moving to IndexedDB. 1440px/q0.7
    // roughly halves per-photo size while staying sharp enough for condition
    // assessment on screen.
    const resized = await resizeImage(dataUrl, 1440, 1440, 0.7);
    resolveCameraCapture(resized);
  } catch (error) {
    console.warn('Échec du traitement de la photo capturée', error);
    resolveCameraCapture(undefined);
  }
};

const pickFile = () => {
  fileInputEl.value?.click();
};

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const onFileSelected = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  // Reset so picking the same file again still fires a change event.
  input.value = '';

  if (!file) {
    return;
  }

  try {
    const dataUrl = await readFileAsDataUrl(file);
    const resized = await resizeImage(dataUrl, 1440, 1440, 0.7);
    resolveCameraCapture(resized);
  } catch (error) {
    // Corrupt file, or not actually a decodable image — back out instead
    // of leaving the capture screen hanging forever with no feedback.
    console.warn('Fichier illisible ou invalide', error);
    resolveCameraCapture(undefined);
  }
};

const cancel = () => {
  resolveCameraCapture(undefined);
};
</script>

<style scoped>
.camera-capture {
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: #000;
}

.camera-capture__video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.camera-capture__bar {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.85);
}

.camera-capture__close,
.camera-capture__file {
  border: none;
  background: none;
  color: #fff;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
}

.camera-capture__spacer {
  width: 48px;
  height: 48px;
}

.camera-capture__shutter {
  width: 76px;
  height: 76px;
  border-radius: 50%;
  border: 4px solid #fff;
  background: rgba(255, 255, 255, 0.25);
  padding: 0;
}

.camera-capture__shutter:active {
  background: rgba(255, 255, 255, 0.5);
}

.camera-capture__shutter:disabled {
  opacity: 0.4;
}

.camera-capture__file-input {
  display: none;
}

/* Portrait (default): thin bar at the top for close, thick bar at the
   bottom for file/shutter — mirrors the native camera app's layout. */
.camera-capture__bar--secondary {
  top: 0;
  left: 0;
  right: 0;
  height: calc(env(safe-area-inset-top, 0px) + 56px);
  justify-content: flex-start;
  padding-left: 16px;
}

.camera-capture__bar--primary {
  bottom: 0;
  left: 0;
  right: 0;
  height: calc(env(safe-area-inset-bottom, 0px) + 116px);
  padding: 0 24px;
  justify-content: space-between;
}

/* Landscape: same two bars, rotated to left (close) / right (file+shutter). */
@media (orientation: landscape) {
  .camera-capture__bar--secondary {
    top: 0;
    bottom: 0;
    left: 0;
    right: auto;
    height: auto;
    width: calc(env(safe-area-inset-left, 0px) + 56px);
    justify-content: flex-start;
    align-items: flex-start;
    padding-left: 0;
    padding-top: 16px;
  }

  .camera-capture__bar--primary {
    top: 0;
    bottom: 0;
    left: auto;
    right: 0;
    height: auto;
    width: calc(env(safe-area-inset-right, 0px) + 116px);
    flex-direction: column;
    padding: 24px 0;
  }
}
</style>
